import React from 'react';
import { motion } from 'framer-motion';
import ImageSlider from './ImageSlider';

const INSPECTION_CATEGORIES = [
  {
    num: '01',
    icon: '🏗️',
    title: 'Lifts & Hoists',
    items: [
      'Asset identification and safe working load verification',
      'Mechanical integrity inspection',
      'Safety functions, interlocks, brakes and limit switches',
      'Functional checks and applicable load testing',
    ],
    highlight: false,
  },
  {
    num: '02',
    icon: '🏗️',
    title: 'Lifting Machinery & Tackles',
    items: [
      'Mobile, tower, EOT and gantry cranes',
      'Winches, chain blocks and electric hoists',
      'Hooks, shackles, chains and wire ropes',
      'Web/round slings and other lifting accessories',
      'Condition, traceability, safety devices and applicable testing',
    ],
    highlight: true,
  },
  {
    num: '03',
    icon: '🌡️',
    title: 'Pressure Plant',
    items: [
      'Physical condition and corrosion assessment',
      'Pressure gauges, relief devices and protection systems',
      'Ultrasonic thickness measurement',
      'Pressure testing and NDT where applicable',
    ],
    highlight: false,
  },
  {
    num: '04',
    icon: '🦺',
    title: 'Safety Belts & Fall Protection',
    items: [
      'Harnesses, belts and lanyards',
      'Webbing, stitching and hardware inspection',
      'Energy absorbers, lifelines and retractable devices',
      'Traceability, rejection criteria and pre-use-check coaching',
    ],
    highlight: false,
  },
];

const DELIVERABLES = [
  { icon: '📸', label: 'Photo-supported inspection reports' },
  { icon: '⚠️', label: 'Defect classification' },
  { icon: '✅', label: 'Conformity status' },
  { icon: '🔧', label: 'Corrective recommendations' },
  { icon: '📜', label: 'Applicable certificates & statutory records' },
];

const container = { animate: { transition: { staggerChildren: 0.1 } } };
const card = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function Services() {
  return (
    <section id="services" className="section-pad bg-brand-surface relative overflow-hidden">
      <div className="container-xl relative z-10">

        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-10 items-center mb-14">
          <div>
            <motion.span
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              className="section-label"
            >
              <span className="w-8 h-px bg-brand-orange" /> Third-Party Inspection
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="font-heading font-black text-4xl sm:text-5xl text-brand-heading leading-tight mb-4"
            >
              🔍 INSPECT —<br /><span className="text-gradient">Independent</span><br />Inspection Services
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="text-brand-subtle leading-relaxed mb-6 max-w-lg"
            >
              Verify equipment. Identify risks. Strengthen compliance. Our experienced engineers and
              Competent Persons deliver independent, on-site inspection, verification, testing and
              conformity assessment for industrial and infrastructure projects.
            </motion.p>
            <motion.a
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
              href="#contact"
              className="btn-primary"
            >
              Request Inspection ↗
            </motion.a>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block rounded-2xl overflow-hidden border border-gray-200 shadow-card"
          >
            <video src="/videos/vidno3.mp4" autoPlay muted loop playsInline className="w-full h-full object-cover" />
          </motion.div>
        </div>

        {/* Inspection Service Cards */}
        <motion.div
          variants={container}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12"
        >
          {INSPECTION_CATEGORIES.map((s) => (
            <motion.div
              key={s.num}
              variants={card}
              whileHover={{ y: -6, transition: { duration: 0.25 } }}
              className={`group relative rounded-2xl p-6 border transition-all duration-300 cursor-default ${
                s.highlight
                  ? 'bg-orange-50 border-orange-200 hover:border-brand-orange hover:shadow-orange'
                  : 'bg-white border-gray-200 hover:border-orange-200 hover:shadow-card-hover'
              }`}
            >
              <div className="flex items-start justify-between mb-5">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                  s.highlight ? 'bg-orange-100' : 'bg-orange-50 group-hover:bg-orange-100'
                } transition-colors duration-300`}>
                  {s.icon}
                </div>
                <span className="text-brand-orange/30 font-heading font-black text-3xl leading-none">{s.num}</span>
              </div>
              <h3 className="font-heading font-bold text-brand-heading text-lg mb-3 group-hover:text-brand-orange transition-colors duration-300">{s.title}</h3>
              <ul className="space-y-1.5">
                {s.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-brand-subtle text-xs leading-relaxed">
                    <span className="w-4 h-4 rounded bg-orange-100 flex items-center justify-center text-brand-orange text-[9px] shrink-0 mt-0.5">▸</span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Client Deliverables */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-card p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-brand-orange" />
            <p className="text-brand-orange text-xs font-bold tracking-[3px] uppercase">Client Deliverables</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {DELIVERABLES.map((d) => (
              <div key={d.label} className="flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-xl px-4 py-2.5">
                <span className="text-lg">{d.icon}</span>
                <span className="text-brand-heading text-sm font-semibold">{d.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="mt-12">
          <ImageSlider page="services" />
        </div>
      </div>
    </section>
  );
}
